import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Search, Trash2, Building2, X, Check } from "lucide-react";
import { useApi } from "../../hooks/useApi";

/**
 * B2B Partner Portal — admin management panel.
 *
 * Self-contained tab rendered inside MainAdmin. Lets a main_admin create
 * partner accounts and assign organizations to them. Isolated: only talks to
 * the additive /admin/partners* endpoints; does not touch other admin state.
 *
 * Styling matches the dark MainAdmin shell (Tailwind classes already used
 * across that page).
 */
const PartnersManager = ({ isDark = true }) => {
  const api = useApi();

  const [partners, setPartners] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // partner being edited

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [pRes, oRes] = await Promise.all([
        api.get("/api/v1/admin/partners"),
        api.get("/api/v1/admin/organizations"),
      ]);
      setPartners(pRes.data?.data?.partners || []);
      setOrgs(oRes.data?.data?.organizations || []);
    } catch (e) {
      setErr(
        e?.response?.data?.error?.message || "Failed to load partners."
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleDelete = async (partner) => {
    if (
      !window.confirm(
        `Delete partner "${partner.firstName} ${partner.lastName}"? This cannot be undone.`
      )
    )
      return;
    try {
      await api.delete(`/api/v1/admin/partners/${partner._id}`);
      setPartners((prev) => prev.filter((p) => p._id !== partner._id));
    } catch (e) {
      alert(e?.response?.data?.error?.message || "Failed to delete partner.");
    }
  };

  const card = isDark
    ? "bg-white/5 border-white/10"
    : "bg-white border-gray-200";
  const text = isDark ? "text-white" : "text-gray-900";
  const sub = isDark ? "text-gray-400" : "text-gray-600";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-xl font-bold ${text}`}>B2B Partners</h2>
          <p className={`text-sm ${sub}`}>
            Create reseller accounts and assign organizations they manage.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{
            background: "linear-gradient(135deg, #00D1C1, #0F766E)",
          }}
        >
          <Plus className="h-4 w-4" />
          Add Partner
        </button>
      </div>

      {err && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">
          {err}
        </div>
      )}

      {loading ? (
        <div className={`text-sm ${sub} py-10 text-center`}>
          Loading partners…
        </div>
      ) : partners.length === 0 ? (
        <div
          className={`rounded-xl border ${card} py-14 text-center`}
        >
          <Building2 className={`h-8 w-8 mx-auto mb-3 ${sub}`} />
          <p className={`font-semibold ${text}`}>No partners yet</p>
          <p className={`text-sm ${sub} mt-1`}>
            Click “Add Partner” to create your first reseller account.
          </p>
        </div>
      ) : (
        <div className={`rounded-xl border ${card} overflow-hidden`}>
          <table className="w-full">
            <thead>
              <tr
                className={`text-left text-xs uppercase tracking-wide ${sub} ${
                  isDark ? "bg-white/5" : "bg-gray-50"
                }`}
              >
                <th className="px-5 py-3 font-semibold">Partner</th>
                <th className="px-5 py-3 font-semibold">Email</th>
                <th className="px-5 py-3 font-semibold">Organizations</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr
                  key={p._id}
                  className={`border-t ${
                    isDark ? "border-white/5" : "border-gray-100"
                  }`}
                >
                  <td className={`px-5 py-3 font-medium ${text}`}>
                    {p.firstName} {p.lastName}
                  </td>
                  <td className={`px-5 py-3 text-sm ${sub}`}>{p.email}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => setAssignFor(p)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
                      style={{
                        color: "#00D1C1",
                        background: "rgba(0,209,193,0.12)",
                      }}
                    >
                      <Building2 className="h-3.5 w-3.5" />
                      {(p.assignedOrganizations || []).length} assigned · Manage
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        p.isActive
                          ? "text-emerald-400 bg-emerald-500/10"
                          : "text-gray-400 bg-gray-500/10"
                      }`}
                    >
                      {p.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => handleDelete(p)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                      title="Delete partner"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreatePartnerModal
          orgs={orgs}
          api={api}
          isDark={isDark}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            fetchAll();
          }}
        />
      )}

      {assignFor && (
        <AssignOrgsModal
          partner={assignFor}
          orgs={orgs}
          api={api}
          isDark={isDark}
          onClose={() => setAssignFor(null)}
          onSaved={() => {
            setAssignFor(null);
            fetchAll();
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Create Partner modal ---------------- */
const CreatePartnerModal = ({ orgs, api, isDark, onClose, onCreated }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError("");
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError("All fields are required.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/v1/admin/partners", {
        ...form,
        organizationIds: selected,
      });
      onCreated();
    } catch (e) {
      setError(
        e?.response?.data?.error?.message || "Failed to create partner."
      );
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add Partner" isDark={isDark} onClose={onClose} wide>
      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg text-sm bg-red-500/10 text-red-400">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Field label="First name" isDark={isDark}>
          <input className={inputCls(isDark)} value={form.firstName} onChange={set("firstName")} />
        </Field>
        <Field label="Last name" isDark={isDark}>
          <input className={inputCls(isDark)} value={form.lastName} onChange={set("lastName")} />
        </Field>
      </div>
      <Field label="Email" isDark={isDark}>
        <input type="email" className={inputCls(isDark)} value={form.email} onChange={set("email")} />
      </Field>
      <div className="h-3" />
      <Field label="Temporary password" isDark={isDark}>
        <input type="text" className={inputCls(isDark)} value={form.password} onChange={set("password")} placeholder="Min 8 characters" />
      </Field>

      <div className="mt-4">
        <OrgPicker orgs={orgs} selected={selected} setSelected={setSelected} isDark={isDark} />
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className={cancelCls(isDark)}>Cancel</button>
        <button
          onClick={submit}
          disabled={busy}
          className={primaryCls}
          style={{ background: "linear-gradient(135deg, #00D1C1, #0F766E)" }}
        >
          {busy ? "Creating…" : "Create partner"}
        </button>
      </div>
    </ModalShell>
  );
};

/* ---------------- Assign orgs modal ---------------- */
const AssignOrgsModal = ({ partner, orgs, api, isDark, onClose, onSaved }) => {
  const initial = useMemo(
    () =>
      (partner.assignedOrganizations || []).map((o) =>
        typeof o === "object" ? String(o._id) : String(o)
      ),
    [partner]
  );
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    setBusy(true);
    try {
      await api.put(`/api/v1/admin/partners/${partner._id}/organizations`, {
        organizationIds: selected,
      });
      onSaved();
    } catch (e) {
      setError(
        e?.response?.data?.error?.message || "Failed to update assignments."
      );
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`Assign organizations · ${partner.firstName} ${partner.lastName}`}
      isDark={isDark}
      onClose={onClose}
      wide
    >
      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg text-sm bg-red-500/10 text-red-400">
          {error}
        </div>
      )}
      <OrgPicker orgs={orgs} selected={selected} setSelected={setSelected} isDark={isDark} />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className={cancelCls(isDark)}>Cancel</button>
        <button
          onClick={save}
          disabled={busy}
          className={primaryCls}
          style={{ background: "linear-gradient(135deg, #00D1C1, #0F766E)" }}
        >
          {busy ? "Saving…" : `Save (${selected.length})`}
        </button>
      </div>
    </ModalShell>
  );
};

/* ---------------- Org multi-select ---------------- */
const OrgPicker = ({ orgs, selected, setSelected, isDark }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return orgs;
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(s));
  }, [orgs, q]);

  const toggle = (id) => {
    const sid = String(id);
    setSelected((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const sub = isDark ? "text-gray-400" : "text-gray-600";
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>
          Organizations
        </span>
        <span className="text-xs font-semibold" style={{ color: "#00D1C1" }}>
          {selected.length} selected
        </span>
      </div>
      <div className="relative mb-2">
        <Search className={`h-4 w-4 absolute left-3 top-2.5 ${sub}`} />
        <input
          className={`${inputCls(isDark)} pl-9`}
          placeholder="Search organizations…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div
        className={`max-h-60 overflow-y-auto rounded-lg border ${
          isDark ? "border-white/10" : "border-gray-200"
        }`}
      >
        {filtered.length === 0 ? (
          <div className={`text-sm ${sub} py-6 text-center`}>No matches.</div>
        ) : (
          filtered.map((o) => {
            const sid = String(o._id);
            const on = selected.includes(sid);
            return (
              <button
                key={sid}
                onClick={() => toggle(o._id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-left border-b last:border-b-0 ${
                  isDark ? "border-white/5" : "border-gray-100"
                } ${on ? (isDark ? "bg-white/5" : "bg-teal-50") : ""}`}
              >
                <span
                  className={`text-sm ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {o.name || "Unnamed org"}
                  {o.orgPlanSelection ? (
                    <span className={`ml-2 text-xs ${sub}`}>
                      · {o.orgPlanSelection}
                    </span>
                  ) : null}
                </span>
                <span
                  className="flex items-center justify-center h-5 w-5 rounded-md"
                  style={{
                    background: on ? "#00D1C1" : "transparent",
                    border: on ? "none" : "1px solid rgba(148,163,184,0.5)",
                  }}
                >
                  {on && <Check className="h-3.5 w-3.5 text-white" />}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

/* ---------------- shared bits ---------------- */
const ModalShell = ({ title, isDark, onClose, children, wide }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: "rgba(2,6,23,0.6)", backdropFilter: "blur(2px)" }}
    onMouseDown={onClose}
  >
    <div
      className={`w-full ${wide ? "max-w-lg" : "max-w-md"} rounded-2xl border p-6 ${
        isDark ? "bg-[#0b1220] border-white/10" : "bg-white border-gray-200"
      }`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
          {title}
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, isDark, children }) => (
  <label className="block">
    <span
      className={`block text-xs font-semibold mb-1.5 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`}
    >
      {label}
    </span>
    {children}
  </label>
);

const inputCls = (isDark) =>
  `w-full h-10 px-3 rounded-lg text-sm outline-none border ${
    isDark
      ? "bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-[#00D1C1]"
      : "bg-white border-gray-200 text-gray-900 focus:border-[#00D1C1]"
  }`;

const primaryCls =
  "px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60";
const cancelCls = (isDark) =>
  `px-4 py-2 rounded-lg text-sm font-semibold ${
    isDark ? "text-gray-300 hover:bg-white/5" : "text-gray-700 hover:bg-gray-100"
  }`;

export default PartnersManager;
